<template>
    <RouterLink
        v-if="isRouterLink"
        :to="routerLinkPath"
        :class="{
            'dynamic-link-active': isLinkActive,
            'dynamic-link-exact-active': isLinkExactActive,
        }"
    >
        <slot />
    </RouterLink>
    <a
        v-else-if="linkHref.length"
        :href="linkHref"
        :target="isTargetBlank ? '_blank' : null"
        :rel="isExternal ? 'noopener noreferrer' : null"
        :class="{
            'dynamic-link-active': isLinkActive,
            'dynamic-link-exact-active': isLinkExactActive,
        }"
    >
        <slot />
    </a>
    <button v-else>
        <slot />
    </button>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useRoute } from "vue-router";

import { PATHS } from "@/router/paths";
import { isDevelopmentEnv } from "@/shared/utils/environment-variables";

const route = useRoute();

const props = defineProps<{
    path: string | null;
    blank?: boolean;
    forceActive?: boolean;
    isExternal?: boolean;
}>();

const isRouterLink = computed(() => {
    if (!props.path || !environment.isSpa) return false;

    const extractedPathSegment = pathStripped.value.split("/").filter(Boolean)[0];

    const isSegmentInRouterPaths = Object.values(PATHS).some((path) => {
        const pathSegments = path.split("/").filter(Boolean)[0];
        return pathSegments === extractedPathSegment;
    });

    return isSegmentInRouterPaths;
});

const pathStripped = computed(() => {
    if (!props.path) return "";

    const pathWithoutOrigin = props.path.replace(window.location.origin, "");
    const pathWithoutPrefix = pathWithoutOrigin?.replace(environment.sportBetsRootUrl, "/");

    if (isDevelopmentEnv()) {
        return pathWithoutPrefix.replace(/^(?:https?:\/\/)?(?:www\.)?(?:[^/]+\.)+[^/]+/, "");
    }
    return pathWithoutPrefix;
});

const isLinkActive = computed(() => {
    if (!props.path) return false;

    if (props.forceActive) return true;

    const extractedPathSegment = pathStripped.value.split("/").filter(Boolean)[0];

    const isSegmentInPath = route.path.split("/").some((segment) => {
        return segment === extractedPathSegment;
    });

    return isSegmentInPath;
});

const isLinkExactActive = computed(() => {
    if (!props.path) return false;

    return route.path === routerLinkPath.value;
});

const routerLinkPath = computed(() => {
    if (!props.path) return "";

    return pathStripped.value;
});

const linkHref = computed(() => {
    if (!props.path) return "";

    const url = props.path;

    // is user is outside of spa view and link url is in PATHS, add sportBetsRootUrl before the url
    if (!environment.isSpa && Object.values(PATHS).includes(url)) {
        return `${environment.sportBetsRootUrl}${url}`.replace("//", "/");
    }

    if (url.includes(environment.sportBetsRootUrl)) {
        return url.replace(window.location.origin, "");
    } else {
        return pathStripped.value;
    }
});

const isTargetBlank = computed(() => {
    return props.blank || (linkHref.value?.startsWith("http") ?? false);
});
</script>
